/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { AreeDTO } from '../schemas/areeDTO.ts';
import type { CambiamentoStatoRequest } from '../schemas/cambiamentoStatoRequest.ts';
import type { Competenza } from '../schemas/competenza.ts';
import type { CreaRichiestaOptions } from '../schemas/creaRichiestaOptions.ts';
import type { CreaRichiestaRequest } from '../schemas/creaRichiestaRequest.ts';
import type { EsitoRichiestaAgnosticaDTO } from '../schemas/esitoRichiestaAgnosticaDTO.ts';
import type { RichiestaDTO } from '../schemas/richiestaDTO.ts';
import type { TipoComunicazioneDTO } from '../schemas/tipoComunicazioneDTO.ts';
import type { WsProblema } from '../schemas/wsProblema.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Crea una nuova richiesta.
 * @summary Crea Richiesta
 */
export const postFrontTipiDomandaIdRichieste = (
  idTipoDomanda: string,
  creaRichiestaOptions: CreaRichiestaOptions,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaDTO>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${idTipoDomanda}/richieste`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaRichiestaOptions,
    },
    options,
  );
};

export const getPostFrontTipiDomandaIdRichiesteMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontTipiDomandaIdRichieste>>,
    TError,
    { idTipoDomanda: string; data: CreaRichiestaOptions },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontTipiDomandaIdRichieste>>,
  TError,
  { idTipoDomanda: string; data: CreaRichiestaOptions },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontTipiDomandaIdRichieste>>,
    { idTipoDomanda: string; data: CreaRichiestaOptions }
  > = props => {
    const { idTipoDomanda, data } = props ?? {};

    return postFrontTipiDomandaIdRichieste(idTipoDomanda, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontTipiDomandaIdRichiesteMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontTipiDomandaIdRichieste>>
>;
export type PostFrontTipiDomandaIdRichiesteMutationBody = CreaRichiestaOptions;
export type PostFrontTipiDomandaIdRichiesteMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Crea Richiesta
 */
export const usePostFrontTipiDomandaIdRichieste = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontTipiDomandaIdRichieste>>,
    TError,
    { idTipoDomanda: string; data: CreaRichiestaOptions },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontTipiDomandaIdRichiesteMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea una nuova richiesta pratica.
 * @summary Crea Richiesta pratica
 */
export const postFrontTipiDomandaIdPraticaIdRichieste = (
  idTipoDomanda: string,
  idIstanzaProcedimento: string,
  creaRichiestaOptions: CreaRichiestaOptions,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<RichiestaDTO>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${idTipoDomanda}/pratica/${idIstanzaProcedimento}/richieste`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaRichiestaOptions,
    },
    options,
  );
};

export const getPostFrontTipiDomandaIdPraticaIdRichiesteMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontTipiDomandaIdPraticaIdRichieste>>,
    TError,
    {
      idTipoDomanda: string;
      idIstanzaProcedimento: string;
      data: CreaRichiestaOptions;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontTipiDomandaIdPraticaIdRichieste>>,
  TError,
  {
    idTipoDomanda: string;
    idIstanzaProcedimento: string;
    data: CreaRichiestaOptions;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontTipiDomandaIdPraticaIdRichieste>>,
    {
      idTipoDomanda: string;
      idIstanzaProcedimento: string;
      data: CreaRichiestaOptions;
    }
  > = props => {
    const { idTipoDomanda, idIstanzaProcedimento, data } = props ?? {};

    return postFrontTipiDomandaIdPraticaIdRichieste(
      idTipoDomanda,
      idIstanzaProcedimento,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontTipiDomandaIdPraticaIdRichiesteMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof postFrontTipiDomandaIdPraticaIdRichieste>>
  >;
export type PostFrontTipiDomandaIdPraticaIdRichiesteMutationBody =
  CreaRichiestaOptions;
export type PostFrontTipiDomandaIdPraticaIdRichiesteMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Crea Richiesta pratica
 */
export const usePostFrontTipiDomandaIdPraticaIdRichieste = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontTipiDomandaIdPraticaIdRichieste>>,
    TError,
    {
      idTipoDomanda: string;
      idIstanzaProcedimento: string;
      data: CreaRichiestaOptions;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontTipiDomandaIdPraticaIdRichiesteMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Crea una nuova richiesta da modulo agnostico.
 * @summary Crea Richiesta da modulo agnostico
 */
export const postFrontTipiDomandaB2BIdRichieste = (
  idTipoDomanda: string,
  creaRichiestaRequest: CreaRichiestaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<EsitoRichiestaAgnosticaDTO>(
    {
      url: `/framework-cmp-domande/tipi-domanda/b2b/${idTipoDomanda}/richieste`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: creaRichiestaRequest,
    },
    options,
  );
};

export const getPostFrontTipiDomandaB2BIdRichiesteMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontTipiDomandaB2BIdRichieste>>,
    TError,
    { idTipoDomanda: string; data: CreaRichiestaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postFrontTipiDomandaB2BIdRichieste>>,
  TError,
  { idTipoDomanda: string; data: CreaRichiestaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postFrontTipiDomandaB2BIdRichieste>>,
    { idTipoDomanda: string; data: CreaRichiestaRequest }
  > = props => {
    const { idTipoDomanda, data } = props ?? {};

    return postFrontTipiDomandaB2BIdRichieste(
      idTipoDomanda,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type PostFrontTipiDomandaB2BIdRichiesteMutationResult = NonNullable<
  Awaited<ReturnType<typeof postFrontTipiDomandaB2BIdRichieste>>
>;
export type PostFrontTipiDomandaB2BIdRichiesteMutationBody =
  CreaRichiestaRequest;
export type PostFrontTipiDomandaB2BIdRichiesteMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Crea Richiesta da modulo agnostico
 */
export const usePostFrontTipiDomandaB2BIdRichieste = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postFrontTipiDomandaB2BIdRichieste>>,
    TError,
    { idTipoDomanda: string; data: CreaRichiestaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostFrontTipiDomandaB2BIdRichiesteMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Aggiorna lo stato della richiesta modulo associati al tipo domanda
 * @summary Aggiorna lo stato della richiesta modulo associati al tipo domanda
 */
export const patchFrontTipiDomandaIdStato = (
  id: string,
  cambiamentoStatoRequest: CambiamentoStatoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${id}/stato`,
      method: 'patch',
      headers: { 'Content-Type': 'application/json' },
      data: cambiamentoStatoRequest,
    },
    options,
  );
};

export const getPatchFrontTipiDomandaIdStatoMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchFrontTipiDomandaIdStato>>,
    TError,
    { id: string; data: CambiamentoStatoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof patchFrontTipiDomandaIdStato>>,
  TError,
  { id: string; data: CambiamentoStatoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof patchFrontTipiDomandaIdStato>>,
    { id: string; data: CambiamentoStatoRequest }
  > = props => {
    const { id, data } = props ?? {};

    return patchFrontTipiDomandaIdStato(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchFrontTipiDomandaIdStatoMutationResult = NonNullable<
  Awaited<ReturnType<typeof patchFrontTipiDomandaIdStato>>
>;
export type PatchFrontTipiDomandaIdStatoMutationBody = CambiamentoStatoRequest;
export type PatchFrontTipiDomandaIdStatoMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna lo stato della richiesta modulo associati al tipo domanda
 */
export const usePatchFrontTipiDomandaIdStato = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchFrontTipiDomandaIdStato>>,
    TError,
    { id: string; data: CambiamentoStatoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPatchFrontTipiDomandaIdStatoMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco di tutti i tipi comunicazione disponibili al Richiedente
 * @summary Ottiene l'elenco di tutti i tipi comunicazione disponibili al Richiedente
 */
export const getFrontTipiDomandaIdTipiComunicazione = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoComunicazioneDTO[]>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${id}/tipi-comunicazione`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontTipiDomandaIdTipiComunicazioneQueryKey = (id: string) =>
  [`/framework-cmp-domande/tipi-domanda/${id}/tipi-comunicazione`] as const;

export const getGetFrontTipiDomandaIdTipiComunicazioneQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdTipiComunicazione>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontTipiDomandaIdTipiComunicazioneQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaIdTipiComunicazione>>
  > = ({ signal }) =>
    getFrontTipiDomandaIdTipiComunicazione(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontTipiDomandaIdTipiComunicazioneQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdTipiComunicazione>>
>;
export type GetFrontTipiDomandaIdTipiComunicazioneQueryError =
  ErrorType<unknown>;

/**
 * @summary Ottiene l'elenco di tutti i tipi comunicazione disponibili al Richiedente
 */
export const useGetFrontTipiDomandaIdTipiComunicazione = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaIdTipiComunicazione>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdTipiComunicazione>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontTipiDomandaIdTipiComunicazioneQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene l'elenco delle competenze associate ad un tipo domanda
 * @summary Ottiene l'elenco delle competenze associate ad un tipo domanda
 */
export const getFrontTipiDomandaIdCompetenze = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Competenza[]>(
    {
      url: `/framework-cmp-domande/tipi-domanda/${id}/competenze`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontTipiDomandaIdCompetenzeQueryKey = (id: string) =>
  [`/framework-cmp-domande/tipi-domanda/${id}/competenze`] as const;

export const getGetFrontTipiDomandaIdCompetenzeQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaIdCompetenze>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdCompetenze>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdCompetenze>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontTipiDomandaIdCompetenzeQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaIdCompetenze>>
  > = ({ signal }) =>
    getFrontTipiDomandaIdCompetenze(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontTipiDomandaIdCompetenzeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontTipiDomandaIdCompetenze>>
>;
export type GetFrontTipiDomandaIdCompetenzeQueryError = ErrorType<unknown>;

/**
 * @summary Ottiene l'elenco delle competenze associate ad un tipo domanda
 */
export const useGetFrontTipiDomandaIdCompetenze = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaIdCompetenze>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontTipiDomandaIdCompetenze>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontTipiDomandaIdCompetenzeQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene l'elenco delle AOO/uffici associate a dei tipi domanda
 * @summary Ottiene l'elenco delle AOO/uffici associate a dei tipi domanda
 */
export const getFrontTipiDomandaAree = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AreeDTO[]>(
    { url: `/framework-cmp-domande/tipi-domanda/aree`, method: 'get', signal },
    options,
  );
};

export const getGetFrontTipiDomandaAreeQueryKey = () =>
  [`/framework-cmp-domande/tipi-domanda/aree`] as const;

export const getGetFrontTipiDomandaAreeQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaAree>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFrontTipiDomandaAree>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontTipiDomandaAree>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetFrontTipiDomandaAreeQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontTipiDomandaAree>>
  > = ({ signal }) => getFrontTipiDomandaAree(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontTipiDomandaAreeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontTipiDomandaAree>>
>;
export type GetFrontTipiDomandaAreeQueryError = ErrorType<unknown>;

/**
 * @summary Ottiene l'elenco delle AOO/uffici associate a dei tipi domanda
 */
export const useGetFrontTipiDomandaAree = <
  TData = Awaited<ReturnType<typeof getFrontTipiDomandaAree>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getFrontTipiDomandaAree>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontTipiDomandaAreeQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
